import { useRef, useState } from 'react';
import type { CSSProperties } from 'react';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const MONTH_MAP: Record<string, number> = {};
MONTHS.forEach((m, i) => { MONTH_MAP[m.toLowerCase()] = i + 1; });
['january','february','march','april','may','june','july','august','september','october','november','december']
  .forEach((m, i) => { MONTH_MAP[m] = i + 1; });

function formatDate(iso: string | null | undefined): string {
  if (!iso || iso === '1970-01-01') return '';
  const [y, m, d] = iso.split('-');
  return `${d} ${MONTHS[parseInt(m, 10) - 1]} ${y}`;
}

function parseDate(text: string): string | null {
  const t = text.trim();
  if (!t) return '';

  // ISO: yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;

  // "d/dd mmm yyyy" or "dd mmm yyyy"
  const dmy = t.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
  if (dmy) {
    const mon = MONTH_MAP[dmy[2].toLowerCase()];
    if (mon) return `${dmy[3]}-${String(mon).padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
  }

  // dd/mm/yyyy or dd-mm-yyyy or dd.mm.yyyy (day-first convention used in the app)
  const slashed = t.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (slashed) {
    return `${slashed[3]}-${slashed[2].padStart(2, '0')}-${slashed[1].padStart(2, '0')}`;
  }

  return null; // unparseable — locale-dependent Date.parse intentionally omitted
}

export function DatePickerInput({
  value,
  onChange,
  hasError,
  style,
}: {
  value: string;
  onChange: (v: string) => void;
  hasError?: boolean;
  style?: CSSProperties;
}) {
  const dateRef = useRef<HTMLInputElement>(null);
  // null = display formatted ISO value; string = user is actively typing
  const [draft, setDraft] = useState<string | null>(null);

  function openPicker() {
    try {
      dateRef.current?.showPicker();
    } catch {
      dateRef.current?.focus();
    }
  }

  function handleBlur() {
    if (draft === null) return;
    const iso = parseDate(draft);
    if (iso !== null) {
      onChange(iso); // '' clears, valid string sets
    }
    // if null (unparseable) revert silently — draft becomes null → shows previous value
    setDraft(null);
  }

  const displayValue = draft !== null ? draft : formatDate(value);

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', ...style }}>
      <input
        type="text"
        className={`finput${hasError ? ' error' : ''}`}
        value={displayValue}
        placeholder="dd/mm/yyyy"
        maxLength={11}
        onChange={e => setDraft(e.target.value)}
        onBlur={handleBlur}
        style={{ paddingRight: 32, width: '100%', height: '100%', fontSize: 'inherit' }}
      />
      <svg
        onClick={openPicker}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          position: 'absolute',
          right: 8,
          width: 15,
          height: 15,
          color: 'var(--text-muted)',
          cursor: 'pointer',
          pointerEvents: 'all',
          flexShrink: 0,
        }}
      >
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="3" y1="10" x2="21" y2="10" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="16" y1="2" x2="16" y2="6" />
      </svg>
      {/* Hidden native date input — opened by the calendar icon */}
      <input
        ref={dateRef}
        type="date"
        tabIndex={-1}
        value={value}
        onChange={e => { onChange(e.target.value); setDraft(null); }}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 1, height: 1, bottom: 0, left: 0 }}
      />
    </div>
  );
}
